import { createContext, useContext, useEffect } from 'react';
import colours from '../../colours';
import { useAppContext } from '../hooks/useAppContext';
import useLocalStorage from '../hooks/useLocalStorage';
import { isFeatureEnabled } from './Features';
import { getObjectFromStorage } from './LocalStorage';
import { localStorage } from './SafeBrowserStorage';

export const DEFAULT_BACKGROUND_ID = 3;

// Both the background and tint color are stored in local storage so that we can apply it before React is initialized.
// This also allows us to easily change the design in a preview and have it update automatically.
export const COMPANY_BACKGROUND_STORAGE_KEY = 'company-design-background';
export const TINT_COLOR_STORAGE_KEY = 'small-improvements.company_color'; // Uses a different local storage API where the key is not prefixed with the user ID.

const CompanyDesignContext = createContext<{
    background: number;
    setBackground: (background: number) => void;
    tintColor: string;
} | null>(null);

export function applyBackground(background?: number) {
    const classArray = Array.from(document.body.classList);
    for (const cl of classArray) {
        if (cl.startsWith('company-background')) {
            document.body.classList.remove(cl);
        }
    }
    if (background !== undefined) {
        document.body.classList.add(`company-background${background}`);
    }
}

function applyTintColor(tintColor: string | null) {
    if (tintColor && !isFeatureEnabled('FEATURE_layoutRefresh')) {
        document.body.style.setProperty('--companyColor', tintColor);
    } else {
        document.body.style.removeProperty('--companyColor');
    }
}

/**
 * Used to apply the company design stored in local storage before React is initialized.
 * This prevents the background from changing while the page is loading.
 */
export const initCompanyDesign = () => {
    const tintColor = localStorage.getItem(TINT_COLOR_STORAGE_KEY);
    applyTintColor(tintColor);

    const background = getObjectFromStorage(COMPANY_BACKGROUND_STORAGE_KEY, DEFAULT_BACKGROUND_ID);

    if (isFeatureEnabled('FEATURE_layoutRefresh')) {
        applyBackground(background);
    }
};

export function CompanyDesignProvider({ children }: { children: React.ReactNode }) {
    const [background, setBackground] = useLocalStorage(COMPANY_BACKGROUND_STORAGE_KEY, DEFAULT_BACKGROUND_ID);

    const { user } = useAppContext();
    const companyTintColor = user.company.companyDesignSettings.companyColor;
    const companyBackground = user.company.companyDesignSettings.companyBackground;
    const isLayoutRefreshEnabled = user?.company?.enabledFeatures?.FEATURE_layoutRefresh;

    useEffect(() => {
        applyTintColor(companyTintColor);
        // The tint color does not use a hook for now because of incompatible key formats. This means it won't necessarily
        // be updated automatically if it's changed elsewhere.
        if (companyTintColor) {
            localStorage.setItem(TINT_COLOR_STORAGE_KEY, companyTintColor);
        } else {
            localStorage.removeItem(TINT_COLOR_STORAGE_KEY);
        }

        setBackground(companyBackground ?? DEFAULT_BACKGROUND_ID);
    }, [companyTintColor, companyBackground, setBackground, isLayoutRefreshEnabled]);

    useEffect(() => {
        if (isFeatureEnabled('FEATURE_layoutRefresh')) {
            applyBackground(background);
        }
    }, [background, isLayoutRefreshEnabled]);

    return (
        <CompanyDesignContext.Provider
            value={{ background, setBackground, tintColor: companyTintColor ?? colours.SIYellow }}
        >
            {children}
        </CompanyDesignContext.Provider>
    );
}

export const useCompanyDesign = () => {
    const designContext = useContext(CompanyDesignContext);
    if (!designContext) {
        throw new Error('designContext must be used within <CompanyDesignProvider>');
    }
    return designContext;
};
